import {
  Box,
  Checkbox,
  IconButton,
  Menu,
  TableCell,
  TableRow,
  Typography,
} from "@material-ui/core";
import { safeDateFormat } from "../utils";
import FolderImage from "images/folderIcon.svg";
import DotsImage from "images/threeDots.svg";
import React, { useEffect, useRef, useState } from "react";
import { useSnackbar } from "contexts/SnackbarContext";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";

import { postActivity } from "slices/activitySlice";
import EditPermissions from "./EditPermissions";
import { deleteFolder, UpdateFolder } from "./FileApi";
import BookmarkFolder from "./BookmarkFolder";

export default function FolderView({
  folder,
  disputeId,
  classes,
  reload,
  profile,
  allProfiles,
  parent_folder,
  selectedFolders,
  handleChangeOnFolderCheckbox,
  unSelectAll,
  isHidePermissionsFeature = false,
  isFirstLevelFolderOwner = false,
  customDisputeId,
  // Closed case: folders can be opened, nothing else.
  readOnly = false,
}) {
  const history = useHistory();
  const dispatch = useDispatch();
  const { showSnackbar } = useSnackbar();
  const [anchorElLocal, setAnchorElLocal] = useState(null);
  const handleClick = (e) => {
    e.stopPropagation();
    setAnchorElLocal(e.currentTarget);
  };
  const handleClose = () => {
    setAnchorElLocal(null);
  };

  const hasFolderPermission = (targetFolder) => {
    const isUploader =
      parseInt(targetFolder?.uploaded_by?.id || targetFolder?.uploaded_by) === parseInt(profile?.user?.id);
    const isWebnyayOwner =
      targetFolder?.webnyay_owner &&
      (parseInt(targetFolder?.webnyay_owner?.id || targetFolder?.webnyay_owner) === parseInt(profile?.id) ||
        parseInt(targetFolder?.webnyay_owner?.id || targetFolder?.webnyay_owner) === parseInt(profile?.user?.id));
    const isAllowedUser =
      Array.isArray(targetFolder?.webnyay_allowed_users) &&
      targetFolder.webnyay_allowed_users.some(
        (u) => parseInt(u?.id || u) === parseInt(profile?.id) || parseInt(u?.id || u) === parseInt(profile?.user?.id)
      );

    return isUploader || isWebnyayOwner || isAllowedUser;
  };

  const [buttonsEnabled, setButtonsEnabled] = useState(() => hasFolderPermission(folder));
  useEffect(() => {
    setButtonsEnabled(hasFolderPermission(folder));
  }, [folder, profile]);

  const [openmodalUploadFolder, setOpenmodalUploadFolder] = useState(false);
  const handleOpenUploadFolderModal = () => {
    const isAdminRole =
      profile?.profile_type === "case_manager" ||
      profile?.profile_type === "sub_admin_tiac_rv";

    if (hasFolderPermission(folder) || isAdminRole) {
      setOpenmodalUploadFolder(true);
    } else showSnackbar("Only the owner can edit permissions.", "warning");
  };
  const handleCloseUploadFolderModalAndRefresh = () => {
    reload();
    setOpenmodalUploadFolder(false);
    setAnchorElLocal(null);
  };
  const handleCloseUploadFolderModal = () => {
    setOpenmodalUploadFolder(false);
    setAnchorElLocal(null);
  };

  const folderName = folder.nameToShowUser || folder.name || "";

  // Extract folder prefix if it exists
  let prefix = "";
  if (folderName) {
    const prefixIndex = folderName.indexOf("__");
    if (prefixIndex !== -1) {
      prefix = folderName.substring(0, prefixIndex + 2);
    }
  }

  // The case number (eg. "AC No. 383/2026_") sits right after the prefix
  const caseNumberPrefix = customDisputeId ? `${customDisputeId}_` : "";
  const fullPrefix =
    caseNumberPrefix && folderName.substring(prefix.length).startsWith(caseNumberPrefix)
      ? prefix + caseNumberPrefix
      : prefix;

  // Extract the clean, editable folder name
  const localName = folderName ? folderName.substring(fullPrefix.length) : "";

  const [rename, setRename] = useState(false);
  const [name, setName] = useState(localName);
  const handleFolderNameChange = (e) => {
    setName(e.target.value);
  };
  const inputRef = useRef(null);
  const handleOnBlur = (e) => {
    setRename(false);
    if (name !== localName) {
      const previousName = folderName;
      const updatedName = fullPrefix + name;
      UpdateFolder(folder.id, {
        name: updatedName,
        nameToShowUser: updatedName,
      }).then(() => {
        dispatch(
          postActivity(
            `renamed a folder ${previousName} to ${updatedName}`,
            0,
            false,
            updatedName,
            disputeId,
            [],
            null,
            folder.id
          )
        );
        reload();
      });
    }
  };

  const handleRename = () => {
    if (buttonsEnabled) {
      handleClose();
      setRename(true);
      setTimeout(() => {
        inputRef.current.focus();
        inputRef.current.select();
      }, 100);
    } else {
      showSnackbar("You don't have edit permissions.", "warning");
    }
  };

  const handleDelete = () => {
    if (buttonsEnabled) {
      deleteFolder(folder.id).then(() => {
        unSelectAll();
        reload();
      });
    } else {
      showSnackbar("You don't have delete permissions.", "warning");
    }
  };

  const handleOpenFolder = (id) => {
    history.push(`/disputes/${disputeId}/files/${id}`);
  };

  const isSelected = selectedFolders?.includes(`${folder.id}`);

  const isFirstLevelFolder = !parent_folder;

  const canModifyFolder = buttonsEnabled && !folder?.system_generated && !readOnly;
  const canBookmark = isFirstLevelFolderOwner && !readOnly;
  const canEditPermissions =
    (hasFolderPermission(folder) ||
      profile?.profile_type === "case_manager" ||
      profile?.profile_type === "sub_admin_tiac_rv") &&
    !isHidePermissionsFeature &&
    !readOnly;

  const showOptionsMenu =
    !isFirstLevelFolder &&
    (canModifyFolder || canBookmark || canEditPermissions);

  return (
    <>
      {/* A row of the Files table; the menu and dialog below render outside
          it (portals), so a double-click inside them can't open the folder. */}
      <TableRow
        className={classes.filesHover}
        onDoubleClick={(e) => {
          handleOpenFolder(folder.id);
        }}
      >
        <TableCell>
          <div style={{ display: "flex", alignItems: "center" }}>
            <Checkbox
              id={folder.id}
              color="primary"
              className={
                isSelected ? classes.visibility : classes.visibilityHidden
              }
              style={{ padding: "5px", height: "fit-content", flexShrink: 0 }}
              checked={isSelected}
              onChange={handleChangeOnFolderCheckbox}
            />
            <span
              style={{ display: "flex", alignItems: "center", flexShrink: 0 }}
            >
              <img src={FolderImage} alt="folder" />
            </span>
            <Typography
              className={classes.tableRowContent}
              style={{ marginLeft: "1rem", minWidth: 0 }}
            >
              {!rename ? (
                // A click on the name opens the folder, as a click on a file's
                // name downloads it.
                <span
                  className={classes.fileFolderName}
                  onClick={() => handleOpenFolder(folder.id)}
                >
                  {folder.nameToShowUser || folder.name}
                </span>
              ) : (
                <input
                  ref={inputRef}
                  value={name}
                  style={{ width: "70%" }}
                  onChange={handleFolderNameChange}
                  onBlur={handleOnBlur}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleOnBlur();
                    }
                  }}
                />
              )}
            </Typography>
          </div>
        </TableCell>
        <TableCell>
          <Typography className={classes.tableRowContent}>
            {folder?.owner_name}
          </Typography>
        </TableCell>
        {profile.profile_type === "case_manager" && (
          <TableCell>
            <Typography className={classes.tableRowContent}>
              {safeDateFormat(folder.modified, "dd/MM/yyyy, HH:mm")}
            </Typography>
          </TableCell>
        )}
        <TableCell>
          <Typography className={classes.tableRowContent}>
            {safeDateFormat(folder.created, "dd/MM/yyyy, HH:mm")}
            {showOptionsMenu && (
              <IconButton
                onClick={handleClick}
                aria-controls={`three-dots-menu${folder.id}`}
                aria-haspopup="true"
                style={{ padding: 0, float: "right" }}
              >
                <img src={DotsImage} alt="options" />
              </IconButton>
            )}
          </Typography>
        </TableCell>
      </TableRow>
      <Menu
        id={`three-dots-menu${folder.id}`}
        anchorEl={anchorElLocal}
        keepMounted
        open={Boolean(anchorElLocal)}
        onClose={handleClose}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        style={{ padding: 0 }}
      >
        <Box className={classes.menuwidth}>
          <Box>
            {canModifyFolder && (
              <>
                <Typography
                  className={classes.filesandfolder}
                  onClick={handleRename}
                >
                  Rename
                </Typography>
                <Typography
                  className={classes.filesandfolder}
                  onClick={handleDelete}
                >
                  Delete
                </Typography>
              </>
            )}
            {canBookmark && (
              <BookmarkFolder
                folder={folder}
                reload={reload}
                closeMenu={handleClose}
                className={classes.filesandfolder}
              />
            )}
            {canEditPermissions && (
              <Typography
                className={classes.filesandfolder}
                onClick={handleOpenUploadFolderModal}
              >
                Edit permissions
              </Typography>
            )}
          </Box>
        </Box>
      </Menu>
      {openmodalUploadFolder && (
        <EditPermissions
          openmodal={openmodalUploadFolder}
          handleCloseModal={handleCloseUploadFolderModal}
          handleCloseModalRefresh={handleCloseUploadFolderModalAndRefresh}
          files={[]}
          folders={[folder]}
          allProfiles={allProfiles}
          parent_folder={parent_folder}
          disputeId={disputeId}
          profile={profile}
          viewPermission={folder.view_permission}
          editPermission={folder.edit_permission}
        />
      )}
    </>
  );
}
